import type { SystemHealth } from '@oasis/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Archive,
  Blocks,
  HardDrive,
  Inbox,
  Lock,
  Server,
  ShieldCheck,
} from 'lucide-react';
import type { User } from 'oidc-client-ts';
import type { ReactElement } from 'react';

import {
  ActionBar,
  DataTable,
  EmptyState,
  ErrorMessage,
  formatVersion,
  KpiGrid,
  Loading,
  PageHeader,
  Panel,
  StatCard,
  StatusBadge,
  type Column,
  type Tone,
} from '../design/components.js';
import { api } from '../runtime.js';

import {
  BACKUP_LABELS,
  SERVICE_LABELS,
  daysUntil,
  formatDate,
  statusInfo,
  statusRank,
  statusRowTone,
} from './platform.js';

const label = { ok: 'OK', degraded: 'dégradé', down: 'en panne' } as const;

type Service = SystemHealth['services'][number];
type Backup = SystemHealth['backups'][number];
type Certificate = SystemHealth['certificates'][number];

/** Une sauvegarde est en retard au-delà de 36 h sans réussite (sauvegarde quotidienne). */
const BACKUP_LATE_HOURS = 36;

function backupState(b: Backup, now: number): { tone: Tone; reason: string } {
  if (b.status !== 'ok') {
    const s = statusInfo(b.status);
    return { tone: s.tone, reason: s.label };
  }
  if (b.last_success_at === null || b.last_success_at === undefined)
    return { tone: 'critical', reason: 'Jamais réussie' };
  const hours = (now - new Date(b.last_success_at).getTime()) / 3_600_000;
  if (hours > BACKUP_LATE_HOURS)
    return { tone: 'warning', reason: `Dernière réussite il y a ${String(Math.round(hours))} h` };
  if (b.last_restore_test_ok === false)
    return { tone: 'warning', reason: 'Dernier essai de restauration en échec' };
  return { tone: 'ok', reason: 'À jour' };
}

function certificateTone(days: number): Tone {
  if (days < 7) return 'critical';
  if (days < 21) return 'warning';
  return 'ok';
}

/**
 * Santé de la plateforme : services, sauvegardes, certificats et file des événements, avec
 * la raison de chaque état (vert / orange / rouge). Données de `/system/health` seulement.
 */
export function HealthPage({ user }: { user: User }): ReactElement {
  const health = useQuery<SystemHealth>({
    queryKey: ['/system/health'],
    queryFn: () => api<SystemHealth>(user, '/system/health'),
    refetchInterval: 60_000,
  });
  if (health.isPending) return <Loading />;
  if (health.isError) return <ErrorMessage error={health.error} />;
  const h = health.data;
  const now = Date.now();
  const failing = h.services.filter((s) => s.status !== 'ok');
  const backupsLate = h.backups.filter((b) => backupState(b, now).tone !== 'ok');
  const certSoon = h.certificates.filter((c) => daysUntil(c.expires_at, now) < 21);
  const nextCert = [...h.certificates].sort((a, b) => a.expires_at.localeCompare(b.expires_at))[0];
  const outboxFailed = h.outbox?.failed ?? 0;
  const outboxPending = h.outbox?.pending ?? 0;
  const enabledModules = h.modules.filter((m) => m.status === 'enabled').length;

  const serviceColumns: Column<Service>[] = [
    {
      key: 'name',
      header: 'Service',
      primary: true,
      sortValue: (s) => SERVICE_LABELS[s.name] ?? s.name,
      render: (s) => (
        <span className="o-cell-title">
          <strong>{SERVICE_LABELS[s.name] ?? s.name}</strong>
          <span className="o-hint">{s.name}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'État',
      sortValue: (s) => statusRank(s.status),
      render: (s) => <StatusBadge status={s.status} {...statusInfo(s.status)} />,
    },
    { key: 'detail', header: 'Raison', render: (s) => s.detail ?? '—' },
  ];
  const backupColumns: Column<Backup>[] = [
    {
      key: 'target',
      header: 'Sauvegarde',
      primary: true,
      sortValue: (b) => BACKUP_LABELS[b.target] ?? b.target,
      render: (b) => (
        <span className="o-cell-title">
          <strong>{BACKUP_LABELS[b.target] ?? b.target}</strong>
          <span className="o-hint">{b.target}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'État',
      render: (b) => {
        const s = backupState(b, now);
        return <StatusBadge status={b.status} tone={s.tone} label={s.reason} />;
      },
    },
    {
      key: 'last',
      header: 'Dernière réussite',
      sortValue: (b) => b.last_success_at ?? '',
      render: (b) => formatDate(b.last_success_at, 'jamais'),
    },
    {
      key: 'restore',
      header: 'Essai de restauration',
      secondary: true,
      sortValue: (b) => b.last_restore_test_at ?? '',
      render: (b) =>
        b.last_restore_test_at === null || b.last_restore_test_at === undefined ? (
          'jamais'
        ) : (
          <span className="o-row">
            {formatDate(b.last_restore_test_at)}
            {b.last_restore_test_ok === true && (
              <StatusBadge status="ok" tone="ok" label="Réussi" />
            )}
            {b.last_restore_test_ok === false && (
              <StatusBadge status="echec" tone="critical" label="Échec" />
            )}
          </span>
        ),
    },
  ];
  const certColumns: Column<Certificate>[] = [
    { key: 'domain', header: 'Domaine', primary: true, sortValue: (c) => c.domain },
    {
      key: 'expires',
      header: 'Expiration',
      sortValue: (c) => c.expires_at,
      render: (c) => {
        const d = daysUntil(c.expires_at, now);
        return (
          <span className="o-row">
            {formatDate(c.expires_at)}
            <StatusBadge
              status="certificat"
              tone={certificateTone(d)}
              label={d < 0 ? 'Expiré' : `Dans ${String(d)} j`}
            />
          </span>
        );
      },
    },
  ];

  return (
    <div className="o-stack">
      <PageHeader
        title={`Santé de la plateforme — ${label[h.status]}`}
        description={`Version ${formatVersion(h.version)} · ${h.mode === 'production' ? 'production' : 'préproduction'}. Actualisé chaque minute.`}
        actions={
          <ActionBar
            more={[
              { label: 'Modules installés', to: '/modules' },
              { label: 'Reprise de l’existant', to: '/reprise' },
            ]}
          />
        }
      />
      <KpiGrid label="État de la plateforme">
        <StatCard
          label="Services"
          icon={Server}
          value={`${String(h.services.length - failing.length)} / ${String(h.services.length)}`}
          tone={
            failing.length === 0
              ? 'ok'
              : failing.some((s) => s.status === 'down')
                ? 'critical'
                : 'warning'
          }
          detail={
            failing.length === 0
              ? 'Tous opérationnels'
              : `En défaut : ${failing.map((s) => SERVICE_LABELS[s.name] ?? s.name).join(', ')}`
          }
        />
        <StatCard
          label="Sauvegardes"
          icon={Archive}
          value={`${String(h.backups.length - backupsLate.length)} / ${String(h.backups.length)}`}
          tone={h.backups.length === 0 ? 'warning' : backupsLate.length === 0 ? 'ok' : 'warning'}
          detail={
            h.backups.length === 0
              ? 'Aucune sauvegarde déclarée'
              : backupsLate.length === 0
                ? 'Toutes à jour'
                : `À vérifier : ${backupsLate.map((b) => BACKUP_LABELS[b.target] ?? b.target).join(', ')}`
          }
        />
        <StatCard
          label="Certificats"
          icon={Lock}
          value={h.certificates.length}
          tone={certSoon.length === 0 ? 'ok' : 'warning'}
          detail={
            nextCert === undefined
              ? 'Aucun certificat suivi'
              : `Prochaine expiration : ${formatDate(nextCert.expires_at)}`
          }
        />
        <StatCard
          label="Événements en échec"
          icon={Inbox}
          value={outboxFailed}
          tone={outboxFailed === 0 ? 'ok' : 'critical'}
          detail={`${String(outboxPending)} en attente`}
        />
        <StatCard
          label="Modules actifs"
          icon={Blocks}
          value={`${String(enabledModules)} / ${String(h.modules.length)}`}
          link="/modules"
        />
      </KpiGrid>
      <Panel title="Services" icon={Activity} count={h.services.length}>
        <DataTable
          caption="Services de la plateforme"
          columns={serviceColumns}
          rows={h.services}
          rowKey={(s) => s.name}
          initialSort={{ key: 'status', direction: 'desc' }}
          rowTone={(s) => statusRowTone(s.status)}
          empty={<EmptyState title="Aucun service suivi." icon={Server} />}
        />
      </Panel>
      <div className="o-grid--2">
        <Panel title="Sauvegardes" icon={HardDrive} count={h.backups.length}>
          <DataTable
            caption="Dernières sauvegardes"
            columns={backupColumns}
            rows={h.backups}
            rowKey={(b) => b.target}
            rowTone={(b) => {
              const t = backupState(b, now).tone;
              return t === 'critical' || t === 'warning' ? t : undefined;
            }}
            empty={<EmptyState title="Aucune sauvegarde déclarée." icon={Archive} />}
          />
        </Panel>
        <Panel title="Certificats" icon={ShieldCheck} count={h.certificates.length}>
          <DataTable
            caption="Certificats des domaines"
            columns={certColumns}
            rows={h.certificates}
            rowKey={(c) => c.domain}
            initialSort={{ key: 'expires', direction: 'asc' }}
            empty={<EmptyState title="Aucun certificat suivi." icon={Lock} />}
          />
        </Panel>
      </div>
    </div>
  );
}
